import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_API_URL || "";

const AdminVariants = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  // New Variant Form State
  const [newVariant, setNewVariant] = useState({
    colorName: "",
    colorHex: "#000000",
    price: "",
    skus: {
      XS: 0,
      S: 0,
      M: 0,
      L: 0,
      XL: 0,
      XXL: 0,
    },
  });

  // State for image uploads (keyed by variantId)
  const [uploadingImages, setUploadingImages] = useState({});

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/products/details/${id}`);
      setProduct(res.data);
    } catch (error) {
      toast.error("Failed to load product");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewVariant((prev) => ({ ...prev, [name]: value }));
  };

  const handleSkuChange = (size, value) => {
    setNewVariant((prev) => ({
      ...prev,
      skus: { ...prev.skus, [size]: Number(value) },
    }));
  };

  const handleAddVariant = async (e) => {
    e.preventDefault();
    if (!newVariant.colorName || !newVariant.price) {
      toast.error("Please fill in color name and price");
      return;
    }

    // Transform skus state into the array format expected by backend
    const skuArray = Object.entries(newVariant.skus)
      .filter(([_, qty]) => qty > 0)
      .map(([size, qty], index) => ({
        order: index,
        quantity: qty,
        sizes: size,
        value: size // Assuming value is same as size for now
      }));

    const payload = {
      color: {
        name: newVariant.colorName,
        hex: newVariant.colorHex
      },
      variantPrice: Number(newVariant.price),
      skus: skuArray,
      images: [] // Images added separately
    };

    try {
      await axios.post(`${API_URL}/api/products/${id}/variants`, payload);
      toast.success("Variant added successfully");
      setNewVariant({
        colorName: "",
        colorHex: "#000000",
        price: "",
        skus: { XS: 0, S: 0, M: 0, L: 0, XL: 0, XXL: 0 },
      });
      fetchProduct();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to add variant");
    }
  };

  const handleDeleteVariant = async (variantId) => {
    if (!window.confirm("Are you sure you want to delete this variant?")) return;
    try {
      await axios.delete(`${API_URL}/api/products/${id}/variants/${variantId}`);
      toast.success("Variant deleted");
      fetchProduct();
    } catch (error) {
      console.error("Delete variant error:", error);
      toast.error(error.response?.data?.message || "Failed to delete variant");
    }
  };

  const handleImageUpload = async (variantId, e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
      formData.append("images", files[i]);
    }

    setUploadingImages(prev => ({ ...prev, [variantId]: true }));

    try {
      await axios.post(`${API_URL}/api/products/${id}/variants/${variantId}/images`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Images uploaded");
      fetchProduct();
    } catch (error) {
      toast.error("Failed to upload images");
    } finally {
      setUploadingImages(prev => ({ ...prev, [variantId]: false }));
    }
  };

  const handleDeleteImage = async (variantId, imageId) => {
    if (!window.confirm("Delete this image?")) return;
    try {
      await axios.delete(`${API_URL}/api/products/${id}/variants/${variantId}/images/${imageId}`);
      toast.success("Image deleted");
      fetchProduct();
    } catch (error) {
      toast.error("Failed to delete image");
    }
  }

  if (loading) return <div style={styles.center}>Loading...</div>;
  if (!product) return <div style={styles.center}>Product not found</div>;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <Link to="/admin/products" style={styles.backLink}>&larr; Back to Products</Link>
        <h1 style={styles.title}>Manage Variants: {product.model}</h1>
      </div>

      <div style={styles.grid}>
        {/* ADD VARIANT FORM */}
        <div style={styles.card}>
          <h2 style={styles.sectionTitle}>Add New Variant</h2>
          <form onSubmit={handleAddVariant} style={styles.form}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Color Name</label>
              <input
                style={styles.input}
                name="colorName"
                value={newVariant.colorName}
                onChange={handleInputChange}
                placeholder="e.g. Midnight Blue"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Color Hex</label>
              <div style={styles.colorInputWrapper}>
                <input
                  type="color"
                  name="colorHex"
                  value={newVariant.colorHex}
                  onChange={handleInputChange}
                  style={styles.colorPicker}
                />
                <input
                  style={styles.input}
                  name="colorHex"
                  value={newVariant.colorHex}
                  onChange={handleInputChange}
                />
              </div>
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Variant Price</label>
              <input
                type="number"
                style={styles.input}
                name="price"
                value={newVariant.price}
                onChange={handleInputChange}
                placeholder={product.price}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Quantities per Size</label>
              <div style={styles.sizesGrid}>
                {Object.keys(newVariant.skus).map(size => (
                  <div key={size} style={styles.sizeItem}>
                    <span style={styles.sizeLabel}>{size}</span>
                    <input
                      type="number"
                      min="0"
                      style={styles.sizeInput}
                      value={newVariant.skus[size]}
                      onChange={(e) => handleSkuChange(size, e.target.value)}
                    />
                  </div>
                ))}
              </div>
            </div>

            <button type="submit" style={styles.button}>Add Variant</button>
          </form>
        </div>

        {/* VARIANTS LIST */}
        <div style={styles.variantsList}>
          {product.variants && product.variants.map(variant => {
            // Handle legacy color string vs object schema
            const colorResult = typeof variant.color === 'string'
              ? { name: variant.color, hex: '#000000' }
              : variant.color;

            return (
              <div key={variant._id} style={styles.variantCard}>
                <div style={styles.variantHeader}>
                  <div style={styles.variantInfo}>
                    <div style={{ ...styles.colorSwatch, background: colorResult.hex }}></div>
                    <div>
                      <h3 style={styles.variantTitle}>{colorResult.name}</h3>
                      <p style={styles.variantPrice}>${variant.variantPrice}</p>
                    </div>
                  </div>
                  <button onClick={() => handleDeleteVariant(variant._id)} style={styles.deleteButton}>Delete</button>
                </div>

                <div style={styles.variantDetails}>
                  <h4 style={styles.subTitle}>Stock</h4>
                  <div style={styles.stockTags}>
                    {variant.skus.map(sku => (
                      <span key={sku._id} style={styles.skuTag}>
                        <strong>{sku.sizes}</strong>: {sku.quantity}
                      </span>
                    ))}
                  </div>
                </div>

                <div style={styles.variantImages}>
                  <h4 style={styles.subTitle}>Images ({variant.images.length})</h4>
                  <div style={styles.imageGrid}>
                    {variant.images.map(img => (
                      <div key={img._id} style={styles.imageWrapper}>
                        <img src={img.src} alt="variant" style={styles.imageThumbnail} />
                        <button onClick={() => handleDeleteImage(variant._id, img.id)} style={styles.removeImageBtn}>&times;</button>
                      </div>
                    ))}
                    <label style={styles.uploadBtn}>
                      {uploadingImages[variant._id] ? "..." : "+"}
                      <input
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={(e) => handleImageUpload(variant._id, e)}
                        style={{ display: 'none' }}
                        disabled={uploadingImages[variant._id]}
                      />
                    </label>
                  </div>
                </div>
              </div>
            );
          })}
          {(!product.variants || product.variants.length === 0) && (
            <p style={styles.emptyText}>No variants added yet.</p>
          )}
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    padding: "40px",
    background: "#f4f6f8",
    minHeight: "100vh",
  },
  center: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100vh",
    fontSize: "18px",
    color: "#666"
  },
  header: {
    marginBottom: "30px",
  },
  backLink: {
    color: "#666",
    textDecoration: "none",
    fontSize: "14px",
    marginBottom: "10px",
    display: "inline-block"
  },
  title: {
    fontSize: "28px",
    color: "#333",
    margin: 0
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "350px 1fr",
    gap: "30px",
    alignItems: "start",
  },
  card: {
    background: "#fff",
    padding: "24px",
    borderRadius: "12px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
    position: 'sticky',
    top: '20px'
  },
  sectionTitle: {
    fontSize: "18px",
    marginBottom: "20px",
    color: "#333",
    borderBottom: "1px solid #eee",
    paddingBottom: "10px"
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "16px"
  },
  formGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "6px"
  },
  label: {
    fontSize: "14px",
    fontWeight: "500",
    color: "#555"
  },
  input: {
    padding: "10px",
    borderRadius: "6px",
    border: "1px solid #ddd",
    fontSize: "14px",
    width: "100%"
  },
  colorInputWrapper: {
    display: "flex",
    gap: "10px",
    alignItems: "center"
  },
  colorPicker: {
    width: "40px",
    height: "40px",
    padding: "0",
    border: "none",
    cursor: "pointer",
    borderRadius: "4px",
    overflow: "hidden"
  },
  sizesGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "10px"
  },
  sizeItem: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    background: "#f9f9f9",
    padding: "6px 10px",
    borderRadius: "6px"
  },
  sizeLabel: {
    fontSize: "12px",
    fontWeight: "600",
    width: "24px"
  },
  sizeInput: {
    width: "100%",
    padding: "4px",
    border: "1px solid #ddd",
    borderRadius: "4px"
  },
  button: {
    padding: "12px",
    background: "#111",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    fontWeight: "600",
    cursor: "pointer",
    marginTop: "10px"
  },
  variantsList: {
    display: "flex",
    flexDirection: "column",
    gap: "20px"
  },
  variantCard: {
    background: "#fff",
    padding: "24px",
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.03)"
  },
  variantHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
    paddingBottom: "15px",
    borderBottom: "1px solid #f0f0f0"
  },
  variantInfo: {
    display: "flex",
    alignItems: "center",
    gap: "15px"
  },
  colorSwatch: {
    width: "30px",
    height: "30px",
    borderRadius: "50%",
    border: "1px solid #eee",
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
  },
  variantTitle: {
    fontSize: "16px",
    margin: 0,
    fontWeight: "600"
  },
  variantPrice: {
    fontSize: "14px",
    color: "#666",
    margin: 0
  },
  deleteButton: {
    padding: "6px 12px",
    background: "#ffebee",
    color: "#d32f2f",
    border: "none",
    borderRadius: "4px",
    fontSize: "12px",
    cursor: "pointer",
    fontWeight: "600"
  },
  variantDetails: {
    marginBottom: "20px"
  },
  subTitle: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#444",
    marginBottom: "10px"
  },
  stockTags: {
    display: "flex",
    gap: "8px",
    flexWrap: "wrap"
  },
  skuTag: {
    background: "#f0f0f0",
    padding: "4px 8px",
    borderRadius: "4px",
    fontSize: "13px",
    color: "#555"
  },
  imageGrid: {
    display: "flex",
    gap: "10px",
    flexWrap: "wrap"
  },
  imageWrapper: {
    position: "relative",
    width: "80px",
    height: "80px",
    borderRadius: "6px",
    overflow: "hidden",
    border: "1px solid #eee"
  },
  imageThumbnail: {
    width: "100%",
    height: "100%",
    objectFit: "cover"
  },
  removeImageBtn: {
    position: "absolute",
    top: "2px",
    right: "2px",
    background: "rgba(0,0,0,0.5)",
    color: "#fff",
    border: "none",
    width: "20px",
    height: "20px",
    borderRadius: "50%",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "14px"
  },
  uploadBtn: {
    width: "80px",
    height: "80px",
    borderRadius: "6px",
    border: "2px dashed #ddd",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    fontSize: "24px",
    color: "#ccc",
    background: "#fafafa"
  },
  emptyText: {
    textAlign: "center",
    color: "#888",
    padding: "40px"
  }
};

export default AdminVariants;
